/**
 * Pure helpers for the correct answer of a quiz question in the admin editor.
 *
 * Stored convention (what the editor writes and the backend grader reads, see
 * backend/src/routes/quizzes.ts, "Normalize stored correct to a boolean"):
 *   - single:     option index (number)
 *   - multi:      option indexes (number[])
 *   - true_false: 0 = Adevărat (true), 1 = Fals (false)
 * Older rows also hold a boolean or the strings "true" / "false" for
 * true_false, and the grader accepts all of them.
 */

export type QuestionKind = "single" | "multi" | "true_false";

/**
 * Turns any stored true_false value into the editor's 0 / 1.
 * Same rule as the grader: boolean as is, number 0 means true, any other
 * number means false, otherwise the string "true" (any case) means true.
 * A missing value (null / undefined) gets the editor default, 0.
 */
export function normalizeTrueFalseIndex(stored: unknown): 0 | 1 {
  if (stored === null || stored === undefined) return 0;
  const isTrue =
    typeof stored === "boolean"
      ? stored
      : typeof stored === "number"
        ? stored === 0
        : String(stored).trim().toLowerCase() === "true";
  return isTrue ? 0 : 1;
}

export type CleanedQuestionAnswers =
  | { ok: true; options: string[]; correct_answer: number | number[] }
  | { ok: false; error: string };

/**
 * Drops empty options before saving and moves the correct indexes with them.
 * Without the remap an empty option in the middle shifts every later index
 * and the wrong option ends up marked correct.
 *
 * true_false keeps its options and answer untouched (its answer is 0 / 1,
 * not an option index). A single or multi question that ends with no correct
 * answer (it pointed only at removed options) returns an error to show.
 */
export function cleanQuestionAnswers(
  kind: QuestionKind,
  options: string[],
  correct: number | number[]
): CleanedQuestionAnswers {
  if (kind === "true_false") {
    return {
      ok: true,
      options: options.filter((o) => o.trim()),
      correct_answer: correct,
    };
  }

  // old index -> new index, only for options that stay.
  const newIndex = new Map<number, number>();
  const kept: string[] = [];
  options.forEach((option, oldIdx) => {
    if (option.trim()) {
      newIndex.set(oldIdx, kept.length);
      kept.push(option);
    }
  });

  if (kind === "multi") {
    const list = Array.isArray(correct) ? correct : [];
    const remapped = Array.from(
      new Set(
        list
          .filter((i) => newIndex.has(i))
          .map((i) => newIndex.get(i) as number)
      )
    ).sort((a, b) => a - b);
    if (remapped.length === 0) {
      return {
        ok: false,
        error:
          "Bifează cel puțin un răspuns corect printre opțiunile completate. Opțiunile goale sunt ignorate la salvare.",
      };
    }
    return { ok: true, options: kept, correct_answer: remapped };
  }

  const single = typeof correct === "number" ? newIndex.get(correct) : undefined;
  if (single === undefined) {
    return {
      ok: false,
      error:
        "Răspunsul corect trebuie să fie o opțiune completată. Opțiunile goale sunt ignorate la salvare.",
    };
  }
  return { ok: true, options: kept, correct_answer: single };
}
